import { Phone, PhoneCall, Ticket, UserRoundCheck, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button, Card } from '@centraoneplus/ui';

interface VoiceChannelOverviewProps {
  /** True when this tenant already has a saved voice configuration. */
  isConfigured: boolean;
  /** True when that configuration has the channel switched on. */
  isEnabled: boolean;
  onNext: () => void;
}

const CAPABILITIES = [
  {
    icon: PhoneCall,
    title: 'Answers every call',
    description:
      'An AI agent picks up, greets the caller in your configured language, and holds a natural conversation.',
  },
  {
    icon: Ticket,
    title: 'Captures what the desk needs',
    description:
      'It asks for the details your ticket type requires and raises the ticket automatically when the call ends.',
  },
  {
    icon: UserRoundCheck,
    title: 'Hands off to a person',
    description:
      'When the caller asks for a human, or the agent cannot help, the call is escalated rather than guessed at.',
  },
];

/**
 * Step one of the Voice Channel setup: what the channel does, before any
 * configuration is asked for.
 *
 * Shown every time Voice Channel is opened, including for tenants that are
 * already configured — the providers appear only after Next. `isConfigured`
 * changes the wording rather than skipping the step.
 */
export default function VoiceChannelOverview({
  isConfigured,
  isEnabled,
  onNext,
}: VoiceChannelOverviewProps) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Phone className="h-5 w-5" />
          Voice Channel
        </h2>
        <p className="text-sm text-gray-500 mt-1 dark:text-gray-400">
          Let customers reach you by phone and have an AI agent handle the call.
        </p>
      </div>

      <Card className="p-6">
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium">How the voice channel works</h3>
            <p className="text-sm text-gray-600 mt-2 leading-relaxed dark:text-gray-300">
              When someone dials your number, the call is streamed to CentraOne rather than to a
              voicemail box. The AI voice agent answers, works out what the caller needs, collects
              the information your team would otherwise have to ring back for, and opens a ticket.
              If the caller wants a person, it says so and steps aside.
            </p>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            {CAPABILITIES.map(({ icon: Icon, title, description }) => (
              <div
                key={title}
                className="rounded-lg border border-gray-200 p-4 dark:border-gray-700"
              >
                <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <h4 className="text-sm font-medium mt-3 text-gray-900 dark:text-white">{title}</h4>
                <p className="text-sm text-gray-500 mt-1 leading-relaxed dark:text-gray-400">
                  {description}
                </p>
              </div>
            ))}
          </div>

          <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800/60">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
              What you will need
            </h4>
            <p className="text-sm text-gray-600 mt-2 leading-relaxed dark:text-gray-300">
              An account with a supported telephony provider and a phone number for customers to
              call. On the next step you pick your provider and configure it. Your greeting,
              language and handoff settings are shared across providers, so switching later does not
              mean starting over.
            </p>
          </div>

          {isConfigured && (
            <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
              <CheckCircle2 className="h-4 w-4" />
              This tenant already has a voice configuration
              {isEnabled ? ' and the channel is switched on.' : ', but the channel is switched off.'}
            </div>
          )}

          <div className="flex justify-end pt-2 border-t border-gray-200 dark:border-gray-700">
            <Button onClick={onNext}>
              {isConfigured ? 'Continue to providers' : 'Next'}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
